import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { ChevronRight, Filter, Layers } from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';
import type { FilterControls } from './config';
import {
  EMPTY_FILTERS,
  STATUS_FILTERS,
  THRESHOLD_KEYS,
  activeFiltersCount,
  moreFiltersCount,
  sameFilters,
  toApiParams,
  type FocusApiParams,
  type ListKey,
  type FocusFilters,
  type ThresholdKey,
} from './search';
import { optionListQuery, type AdNetworkAccount, type FilterType, type FocusAppsResponse } from './api';
import { identityOf } from './apps';
import {
  ASSET_TYPE_LABEL,
  OFFERED_ASSET_TYPES,
  STATUS_FILTER_LABEL,
  accountLabel,
  networkGroups,
  networkOf,
  osOptionLabel,
  toggleNetwork,
} from './filterOptions';
import { AppLogo } from '@/components/brand/AppLogo';
import { NetworkLogo } from '@/components/brand/NetworkLogo';
import { networkName } from '@/components/brand/networks';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Chip } from '@/components/ui/chip';
import { inputClass } from '@/components/ui/input';
import { MultiSelect, type MultiSelectOption } from '@/components/ui/multi-select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/cn';
import { formatCountry } from '@/lib/format';

type FiltersPopoverProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Which fields the screen offers (FocusConfig). */
  controls: FilterControls & { allApps: boolean };
  applied: FocusFilters;
  /** Applied dates: option lists are scoped to them (null on a screen without dates). */
  dates: { from: string; to: string } | null;
  apps: UseQueryResult<FocusAppsResponse>;
  accounts: UseQueryResult<AdNetworkAccount[]>;
  onApply: (filters: FocusFilters) => void;
  /**
   * Open on one field, outlined and focused, with "More filters" expanded —
   * e.g. an insight's "Filter by country" link opens the panel on Country.
   */
  highlight?: 'country_code';
};

/**
 * The Filters chip and panel. Every edit is a draft; Apply commits all of it
 * at once (and only when it would change what the API is asked); Cancel, Esc
 * or clicking away discards it. The App field is the SAME selection as the
 * app button in the top bar. `controls` hides the fields a screen does not
 * offer (All apps, Status, Activity, thresholds); their keys are never in
 * that screen's URL, so hiding them loses nothing.
 */
export function FiltersPopover({ open, onOpenChange, controls, applied, dates, apps, accounts, onApply, highlight }: FiltersPopoverProps) {
  const count = activeFiltersCount(controls.assetTypes === false ? { ...applied, asset_type: [] } : applied);
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Chip icon={<Filter />} label="Filters" count={count} />
      </PopoverTrigger>
      <PopoverContent
        aria-label="Filters"
        width="wide"
        className="p-3"
        onOpenAutoFocus={(event) => {
          if (!highlight) return;
          const target = (event.currentTarget as HTMLElement | null)?.querySelector<HTMLElement>(`[data-filter-field="${highlight}"] button`);
          if (!target) return;
          event.preventDefault();
          target.focus();
          target.scrollIntoView({ block: 'center' });
        }}
      >
        <FiltersPanel
          highlight={highlight}
          controls={controls}
          applied={applied}
          dates={dates}
          apps={apps}
          accounts={accounts}
          onCancel={() => { onOpenChange(false); }}
          onApply={(filters) => {
            onOpenChange(false);
            onApply(filters);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

type Thresholds = Record<ThresholdKey, string>;

const thresholdText = (f: FocusFilters): Thresholds => ({
  min_impressions: f.min_impressions?.toString() ?? '',
  min_clicks: f.min_clicks?.toString() ?? '',
  min_installs: f.min_installs?.toString() ?? '',
  min_cost: f.min_cost?.toString() ?? '',
});

/** A threshold's text → value; `false` when it is not a valid minimum. */
const parseThreshold = (key: ThresholdKey, text: string): number | undefined | false => {
  const trimmed = text.trim();
  if (!trimmed) return undefined;
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value < 0) return false;
  if (key !== 'min_cost' && !Number.isInteger(value)) return false;
  if (key === 'min_cost' && Math.round(value * 100) !== value * 100) return false;
  return value > 0 ? value : undefined;
};

const THRESHOLD_LABEL: Record<ThresholdKey, string> = {
  min_impressions: 'Minimum impressions',
  min_clicks: 'Minimum clicks',
  min_installs: 'Minimum installs',
  min_cost: 'Minimum spend ($)',
};

function FiltersPanel({
  controls,
  applied,
  dates,
  apps,
  accounts,
  onCancel,
  onApply,
  highlight,
}: Omit<FiltersPopoverProps, 'open' | 'onOpenChange'> & { onCancel: () => void }) {
  const [draft, setDraft] = useState<FocusFilters>(applied);
  const [text, setText] = useState<Thresholds>(() => thresholdText(applied));
  const [moreOpen, setMoreOpen] = useState(() => moreFiltersCount(controls.assetTypes === false ? { ...applied, asset_type: [] } : applied) > 0 || highlight === 'country_code');

  const parsed = THRESHOLD_KEYS.map((key) => [key, parseThreshold(key, text[key])] as const);
  const invalid = new Set(parsed.filter(([, v]) => v === false).map(([k]) => k));
  const candidate: FocusFilters = { ...draft };
  for (const [key, value] of parsed) if (value !== false) candidate[key] = value;

  const noApp = !candidate.all_apps && candidate.app_id.length === 0;
  const dirty = !sameFilters(candidate, applied);
  const canApply = dirty && !noApp && invalid.size === 0;
  const more = moreFiltersCount(controls.assetTypes === false ? { ...candidate, asset_type: [] } : candidate);

  const update = (patch: Partial<FocusFilters>) => {
    const next = { ...draft, ...patch };
    setDraft(next);
    if (moreFiltersCount(controls.assetTypes === false ? { ...next, asset_type: [] } : next) > 0) setMoreOpen(true);
  };
  const toggleIn = <T extends string>(list: readonly T[], value: T): T[] =>
    list.includes(value) ? list.filter((x) => x !== value) : [...list, value];

  // While Apply is pending, option lists follow the draft (and are cached under
  // the draft's own params, never as the applied scope's).
  const optionScope: FocusApiParams = toApiParams(
    { ...(dirty ? candidate : applied), status: [], date_from: dates?.from, date_to: dates?.to },
  );

  const appList = apps.data?.apps ?? [];
  const accountList = accounts.data ?? [];
  const groups = networkGroups(accountList);
  const selectedAccounts = new Set(draft.ad_network_account_id);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (canApply) onApply(candidate);
      }}
    >
      <div className="grid grid-cols-1 gap-x-4 gap-y-3.5 p-1.5 min-[620px]:grid-cols-2 min-[900px]:grid-cols-3">
        <Field label={<>App <span className="font-normal text-muted">· same selection as the app button in the top bar</span></>} className="col-span-full">
          <Pills>
            {controls.allApps ? (
              <Pill pressed={Boolean(draft.all_apps)} onClick={() => { update({ all_apps: draft.all_apps ? undefined : '1', app_id: [] }); }}>
                <Layers className="size-4 text-muted" aria-hidden="true" />
                All apps
              </Pill>
            ) : null}
            {appList.map((app) => {
              const id = identityOf(app.id, appList);
              return (
                <Pill
                  key={app.id}
                  pressed={!draft.all_apps && draft.app_id.includes(app.id)}
                  onClick={() => { update({ all_apps: undefined, app_id: toggleIn(draft.all_apps ? [] : draft.app_id, app.id) }); }}
                >
                  <AppLogo name={id.name} iconUrl={id.iconUrl} seed={id.id} size={18} className="rounded-[5px]" />
                  {id.name} <span className="opacity-70">{id.os}</span>
                </Pill>
              );
            })}
            {/* An app named by the URL that is not in the entitled list stays visible and removable. */}
            {draft.app_id
              .filter((idValue) => !appList.some((a) => a.id === idValue))
              .map((idValue) => {
                const id = identityOf(idValue, appList);
                return (
                  <Pill key={idValue} pressed onClick={() => { update({ app_id: draft.app_id.filter((x) => x !== idValue) }); }}>
                    <AppLogo name={id.name} seed={id.id} size={18} className="rounded-[5px]" />
                    {id.name} <span className="opacity-70">{id.os}</span>
                  </Pill>
                );
              })}
          </Pills>
          {noApp ? <span className="text-xs text-neg-ink">{controls.allApps ? 'Pick at least one app, or All apps.' : 'Pick at least one app.'}</span> : null}
        </Field>

        {controls.status ? (
          <Field label="Status">
            <Pills>
              {STATUS_FILTERS.map((status) => (
                <Pill key={status} pressed={draft.status.includes(status)} onClick={() => { update({ status: toggleIn(draft.status, status) }); }}>
                  {STATUS_FILTER_LABEL[status]}
                </Pill>
              ))}
            </Pills>
          </Field>
        ) : null}

        <Field label="Ad network">
          <Pills>
            {groups.map((group) => {
              const on = group.accountIds.some((idValue) => selectedAccounts.has(idValue));
              return (
                <Pill
                  key={group.network}
                  pressed={on}
                  onClick={() => { update({ ad_network_account_id: toggleNetwork(draft.ad_network_account_id, group, !on) }); }}
                >
                  <NetworkLogo network={group.network} size={18} decorative className="border-0 p-px" />
                  {group.name}
                </Pill>
              );
            })}
            {!groups.length ? <span className="text-sm text-muted">{accounts.isPending ? 'Loading…' : 'No enabled accounts'}</span> : null}
          </Pills>
        </Field>

        <Field label="Account" plain>
          <MultiSelect
            label="Account"
            appearance="field"
            allLabel="All accounts"
            value={draft.ad_network_account_id}
            onChange={(value) => { update({ ad_network_account_id: value }); }}
            options={accountList.map<MultiSelectOption>((account) => ({
              value: account.id,
              label: accountLabel(account),
              meta: networkName(networkOf(account)),
              icon: <NetworkLogo network={networkOf(account)} size={16} decorative className="border-0 p-px" />,
            }))}
            emptyMessage={accounts.isPending ? 'Loading…' : 'No accounts match.'}
          />
        </Field>

        <LazyField label="Campaign" allLabel="All campaigns" filterType="campaign_name" field="campaign_name" draft={draft} scope={optionScope} update={update} creatable />
        <LazyField label="Ad group" allLabel="All ad groups" filterType="ad_group_name" field="ad_group_name" draft={draft} scope={optionScope} update={update} creatable />
        <LazyField label="Ad" allLabel="All ads" filterType="ad_group_ad_name" field="ad_group_ad_name" draft={draft} scope={optionScope} update={update} creatable />
      </div>

      <details
        open={moreOpen}
        onToggle={(e) => { setMoreOpen(e.currentTarget.open); }}
        className="mt-1.5 border-t border-line pt-1.5"
      >
        <summary className="flex cursor-pointer list-none items-center gap-1.5 rounded-ctl px-1.5 py-2 text-sm font-semibold hover:bg-wash [&::-webkit-details-marker]:hidden">
          <ChevronRight className={cn('size-4 text-muted transition-transform', moreOpen && 'rotate-90')} aria-hidden="true" />
          More filters{more ? <span className="font-normal text-muted"> ({more} active)</span> : null}
        </summary>
        <div className="grid grid-cols-1 gap-x-4 gap-y-3.5 p-1.5 min-[620px]:grid-cols-2 min-[900px]:grid-cols-3">
          {controls.assetTypes !== false ? <Field label="Asset types">
            <Pills>
              {OFFERED_ASSET_TYPES.map((type) => (
                <Pill key={type} pressed={draft.asset_type.includes(type)} onClick={() => { update({ asset_type: toggleIn(draft.asset_type, type) }); }}>
                  {ASSET_TYPE_LABEL[type]}
                </Pill>
              ))}
              {draft.asset_type
                .filter((type) => !OFFERED_ASSET_TYPES.includes(type))
                .map((type) => (
                  <Pill key={type} pressed onClick={() => { update({ asset_type: draft.asset_type.filter((x) => x !== type) }); }}>
                    {ASSET_TYPE_LABEL[type]}
                  </Pill>
                ))}
            </Pills>
          </Field> : null}
          <LazyField label="Country" allLabel="All countries" filterType="country" field="country_code" draft={draft} scope={optionScope} update={update} format={formatCountry} highlighted={highlight === 'country_code'} />
          <LazyField label="Placement" allLabel="All placements" filterType="platform" field="platform" draft={draft} scope={optionScope} update={update} />
          <LazyField label="Operating system" allLabel="All operating systems" filterType="os" field="os" draft={draft} scope={optionScope} update={update} format={osOptionLabel} />
          {controls.activity ? (
            <Field label="Activity" plain>
              <Checkbox
                checked={Boolean(draft.active_during_past_n_hours)}
                onCheckedChange={(on) => { update({ active_during_past_n_hours: on ? 24 : undefined }); }}
                label="Impressions in the last 24 hours"
                className="px-0"
              />
            </Field>
          ) : null}
          {(controls.thresholds ? THRESHOLD_KEYS : []).map((key) => (
            <ThresholdInput
              key={key}
              label={THRESHOLD_LABEL[key]}
              value={text[key]}
              step={key === 'min_cost' ? '0.01' : '1'}
              error={invalid.has(key) ? (key === 'min_cost' ? 'Enter an amount of at least 0, in cents at most.' : 'Enter a whole number of at least 0.') : null}
              onChange={(value) => {
                setText((prev) => ({ ...prev, [key]: value }));
                if (value.trim()) setMoreOpen(true);
              }}
            />
          ))}
        </div>
      </details>

      <div className="mt-2 flex flex-wrap items-center justify-between gap-2 border-t border-line px-1.5 pt-3">
        <Button
          variant="ghost"
          onClick={() => {
            setDraft({ ...EMPTY_FILTERS, app_id: draft.app_id, all_apps: draft.all_apps });
            setText(thresholdText(EMPTY_FILTERS));
          }}
        >
          Clear all
        </Button>
        <span className="flex gap-2">
          <Button onClick={onCancel}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={!canApply}>
            Apply
          </Button>
        </span>
      </div>
    </form>
  );
}

function Field({ label, children, className, plain = false, field, highlighted = false }: { label: ReactNode; children: ReactNode; className?: string; plain?: boolean; field?: string; highlighted?: boolean }) {
  const id = useId();
  return (
    <div
      role={plain ? undefined : 'group'}
      aria-labelledby={plain ? undefined : id}
      data-filter-field={field}
      className={cn('flex min-w-0 flex-col gap-1.5', highlighted && 'rounded-[4px] outline-2 outline-offset-4 outline-blue', className)}
    >
      <span id={id} className="text-xs font-medium text-muted">{label}</span>
      {children}
    </div>
  );
}

const Pills = ({ children }: { children: ReactNode }) => <div className="flex flex-wrap gap-1.5">{children}</div>;

function Pill({ pressed, onClick, children }: { pressed: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      onClick={onClick}
      className="inline-flex h-[26px] items-center gap-1.5 rounded-pill border border-line bg-surface px-2.5 text-xs whitespace-nowrap hover:border-line-strong aria-pressed:border-fg aria-pressed:bg-fg aria-pressed:text-bg"
    >
      {children}
    </button>
  );
}

type LazyFieldProps = {
  label: string;
  /** "All campaigns" — what an empty selection means. */
  allLabel: string;
  filterType: Exclude<FilterType, 'date_range'>;
  field: ListKey;
  draft: FocusFilters;
  scope: FocusApiParams;
  update: (patch: Partial<FocusFilters>) => void;
  creatable?: boolean;
  /** Label for a value (country names, OS spelling). */
  format?: (value: string, supplied?: string) => string;
  /** Outline the field (FiltersPopover `highlight`). */
  highlighted?: boolean;
};

/**
 * A searchable multi-select whose options load on first open, scoped to the
 * draft. A selected value missing from the list stays visible; a capped list
 * says so.
 */
function LazyField({ label, allLabel, filterType, field, draft, scope, update, creatable = false, format, highlighted = false }: LazyFieldProps) {
  const [opened, setOpened] = useState(false);
  // The option list for a field never narrows by that field itself (the API
  // ignores it for its own type), so leave it out of the key.
  const params = Object.fromEntries(Object.entries(scope).filter(([key]) => key !== field));
  const options = useQuery(optionListQuery(filterType, params, opened));
  const selected = draft[field];
  const values = options.data?.values ?? [];
  const known = new Set(values.map((v) => v.value));
  const list: MultiSelectOption[] = [
    ...values.map((v) => ({ value: v.value, label: format ? format(v.value, v.label) : (v.label ?? v.value) })),
    ...selected.filter((v) => !known.has(v)).map((v) => ({ value: v, label: format ? format(v) : v })),
  ];
  return (
    <Field label={label} plain field={field} highlighted={highlighted}>
      <MultiSelect
        label={label}
        appearance="field"
        allLabel={allLabel}
        value={selected}
        onChange={(value) => { update({ [field]: value }); }}
        options={list}
        creatable={creatable}
        onOpenChange={(next) => { if (next) setOpened(true); }}
        emptyMessage={options.isPending && opened ? 'Loading…' : options.isError ? 'Couldn’t load the options. Close and reopen to try again.' : 'No matches.'}
        notice={options.data?.truncated ? 'Showing the first available values. Apply more filters to narrow this list.' : undefined}
      />
    </Field>
  );
}

function ThresholdInput({ label, value, step, error, onChange }: { label: string; value: string; step: string; error: string | null; onChange: (value: string) => void }) {
  const id = useId();
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <label htmlFor={id} className="text-xs font-medium text-muted">{label}</label>
      <input
        id={id}
        type="number"
        inputMode="decimal"
        min={0}
        step={step}
        value={value}
        placeholder="—"
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${id}-error` : undefined}
        onChange={(e) => { onChange(e.target.value); }}
        className={cn(inputClass, 'num h-8 text-sm [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none')}
      />
      {error ? <span id={`${id}-error`} className="text-xs text-neg-ink">{error}</span> : null}
    </div>
  );
}
